import * as React from "react";

import type { QueryTarget } from "@/shared/domain/query";
import { useSubscribeToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  RESOURCE_TABLE_ACTION_TOPIC,
  type ResourceTableActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import type { ResourceView } from "../useResourceView";
import { viewStateToDefinition } from "../viewSerialization";

import type { LoadedView } from "./useLoadedView";

export interface UseResourceTableContextOptions<TData> {
  /** Resource type this list shows. */
  target: QueryTarget;
  /** The live resource view to mirror. */
  view: ResourceView<TData>;
  /**
   * The loaded-View session, for what is loaded, for applying a View the chat
   * hands over, and for unloading one the chat deleted. The same applier the
   * picker uses, so a View arriving from chat is sequenced against a
   * customer's own selection like any other.
   */
  session: Pick<LoadedView, "loadedView" | "selectView" | "clearView">;
}

/**
 * The resource table's two-way link with the AI chat.
 *
 * Out: registers the table's live state as a View definition, through the
 * same serializer a save uses, together with the View loaded into it. The
 * agent can then answer "what am I looking at?" from the definition, and
 * "save this as a View" names exactly what is on screen. Registered whether
 * or not a View is loaded: ad-hoc filters are as much what the customer is
 * looking at as a saved one.
 *
 * In: applies a View the chat publishes for this table, so a View created or
 * changed from chat lands on the table without a navigation. A View for
 * another table is ignored; the publisher checks the table on screen before
 * publishing and navigates instead when it is not this one. Also unloads the
 * loaded View when the chat deletes it, as the picker does after its own
 * delete, so the table does not keep a View that no longer exists.
 *
 * Takes the same table and target as `useRecordTableState`, deliberately:
 * both mirror the same table, one into this tab's memory and one into the
 * chat.
 */
export function useResourceTableContext<TData>({
  target,
  view,
  session,
}: UseResourceTableContextOptions<TData>): void {
  const { filters, table } = view;
  const { sort, pageSize, visibleColumnIds } = table;
  const { loadedView, selectView, clearView } = session;

  const definition = React.useMemo(
    () =>
      viewStateToDefinition({
        filters,
        sort,
        pageSize,
        visibleColumns: [...visibleColumnIds],
      }),
    [filters, sort, pageSize, visibleColumnIds],
  );

  useRegisterViewingContext("resourceTable", {
    target,
    definition,
    ...(loadedView !== null && { loadedView }),
  });

  useSubscribeToAiAgentThreadPubSubBus<ResourceTableActionPayload>(
    RESOURCE_TABLE_ACTION_TOPIC,
    (action) => {
      if (
        action.type === "apply_view" &&
        action.payload.view.target === target
      ) {
        void selectView(action.payload.view);
      }
      if (
        action.type === "view_deleted" &&
        loadedView?.view_id === action.payload.viewId
      ) {
        clearView();
      }
    },
  );
}
